import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const INITIAL = 'input, textarea, select, button:not([data-close])';

export type FocusTrapOptions = {
  active: boolean;
  /** `first`: the first form control / button; `container`: the element itself (needs `tabIndex={-1}`) */
  initialFocus?: 'first' | 'container';
  /** Escape handler; receives the event (already stopped from propagating when it returns `true`) */
  onEscape?: (e: KeyboardEvent) => void;
  /** restore focus to the previously focused element on deactivate (default true) */
  restore?: boolean;
};

/** Active traps, innermost last: only the top one handles keys, so Escape in a nested picker or panel closes just it. */
const stack: object[] = [];

/**
 * Modal focus management shared by Dialog, the asset viewer, QuickLook and the mobile sidebar drawer:
 * moves focus in on activate, keeps Tab inside, restores focus on deactivate. The effect runs on
 * `active` changes only; callbacks are read through a ref so parent re-renders never re-run it (H2).
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, opts: FocusTrapOptions): void {
  const latest = useRef(opts);
  useLayoutEffect(() => {
    latest.current = opts;
  });
  const { active } = opts;
  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;
    const prev = document.activeElement as HTMLElement | null;
    const first = latest.current.initialFocus === 'container' ? null : el.querySelector<HTMLElement>(INITIAL);
    (first ?? el).focus({ preventScroll: true });
    // every trap listens on `document` (capture), where stopPropagation cannot silence the others
    const token = {};
    stack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === 'Escape' && latest.current.onEscape) {
        e.stopPropagation();
        latest.current.onEscape(e);
        return;
      }
      if (e.key !== 'Tab') return;
      // a nested modal (portaled Dialog, Menu) owns focus: let its own trap handle it
      const activeEl = document.activeElement;
      if (!activeEl || !el.contains(activeEl)) return;
      const focusables = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!focusables.length) {
        e.preventDefault();
        return;
      }
      const firstEl = focusables[0]!;
      const lastEl = focusables[focusables.length - 1]!;
      if (e.shiftKey && (activeEl === firstEl || activeEl === el)) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      stack.splice(stack.indexOf(token), 1);
      if (latest.current.restore !== false && prev && prev.isConnected && typeof prev.focus === 'function') prev.focus({ preventScroll: true });
    };
  }, [active, ref]);
}
